'use client';

import { NavigationProgress } from '@/components/layout/navigation/NavigationProgress';
import { ScrollRestoration } from '@/components/layout/navigation/ScrollRestoration';
import { SidebarInset, SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { useChromePathname } from '@/shared/components/providers/chrome-pathname';
import { useImmersiveRoute } from '@/shared/hooks/use-immersive-route';
import { cn } from '@/shared/utils/formatting';
import { usePathname } from 'next/navigation';
import {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { Footer } from './footer';

interface ShellContextValue {
  isImmersive: boolean;
}

const ShellContext = createContext<ShellContextValue | null>(null);

export function useAppShell(): ShellContextValue {
  const ctx = useContext(ShellContext);
  // Safe fallback for components rendered outside AppShell (e.g. storybook)
  return ctx ?? { isImmersive: false };
}

interface AppShellProps {
  header: ReactNode;
  sidebar: ReactNode;
  extraImmersivePatterns?: RegExp[];
  showGlow?: boolean;
  showFooter?: boolean;
  /** Fallback only — real state comes from the sidebar_state/auth_hint
   *  cookies (fix-up script + SidebarProvider initializer). */
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
}

export function AppShell({
  header,
  sidebar,
  extraImmersivePatterns,
  showGlow = false,
  showFooter = true,
  defaultOpen = false,
  className,
  children,
}: AppShellProps) {
  const isImmersive = useImmersiveRoute(extraImmersivePatterns);
  const shellContext = useMemo(() => ({ isImmersive }), [isImmersive]);

  return (
    <ShellContext.Provider value={shellContext}>
      <SidebarProvider
        defaultOpen={defaultOpen}
        headerHeight="4rem"
        className={cn('flex flex-col min-h-dvh', className)}
      >
        {/* Always in the DOM so the pre-paint data-glow guard can reveal it
            before hydration without a structural mismatch. */}
        <div
          data-slot="shell-glow"
          {...(showGlow ? { 'data-visible': '1' } : {})}
          className="absolute inset-0 pointer-events-none bg-glow-hero z-1 hidden data-[visible]:block"
        />
        {header}

        <div className="flex flex-1 min-h-0">
          {sidebar}
          <SidebarInset>
            {/* Transition-diffing components keep the real usePathname()
                (null→real from the chrome context would read as a fake route
                change); Suspense satisfies shell validation. */}
            <Suspense>
              <ScrollRestoration />
            </Suspense>
            <Suspense>
              <NavigationProgress />
            </Suspense>
            <div className="flex-1 min-w-0">
              {children}
              {showFooter && <Footer />}
            </div>
          </SidebarInset>
        </div>

        <SidebarRouteGuard isImmersive={isImmersive} />
        <Suspense>
          <SidebarMobileAutoClose />
        </Suspense>
      </SidebarProvider>
    </ShellContext.Provider>
  );
}

// Mobile drawer is a Sheet whose open state is not router-driven, so menu items
// only navigate and leave it open. Close it on any path change.
function SidebarMobileAutoClose() {
  const { openMobile, setOpenMobile } = useSidebar();
  const pathname = usePathname();
  const prev = useRef(pathname);

  useEffect(() => {
    if (pathname !== prev.current) {
      prev.current = pathname;
      if (openMobile) setOpenMobile(false);
    }
  }, [pathname, openMobile, setOpenMobile]);

  return null;
}

function SidebarRouteGuard({ isImmersive }: { isImmersive: boolean }) {
  const { setOpen } = useSidebar();
  const pathname = useChromePathname();
  // Collapse only on a real navigation into an immersive route: the first
  // pathname resolution after hydration (prevPath null) must not
  // force-collapse a hard load — parity with the pre-restructure behavior,
  // where the guard mounted already knowing the pathname.
  const prevPath = useRef<string | null>(null);
  const prevImmersive = useRef(false);

  useEffect(() => {
    if (prevPath.current !== null && isImmersive && !prevImmersive.current) {
      setOpen(false);
    }
    prevPath.current = pathname;
    prevImmersive.current = isImmersive;
  }, [pathname, isImmersive, setOpen]);

  return null;
}
